import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle2, AlertTriangle, ArrowLeft } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import styles from './Auth.module.css'

type Fase = 'caricamento' | 'attivazione' | 'successo' | 'gia_attivo' | 'token_invalido'

/**
 * Pagina raggiunta cliccando il link di verifica nell'email Supabase.
 *
 * Flusso (lo stesso pattern di ResetPassword):
 *  1. Supabase aggiunge `#access_token=…&type=signup&refresh_token=…` all'URL
 *  2. Leggiamo `access_token` dall'hash (NO @supabase/supabase-js sul frontend)
 *  3. POST /auth/conferma-email con il token → backend valida via Supabase
 *     REST API e marca `users.attivo = true`
 *  4. Schermata di successo + redirect automatico a /accedi dopo 3s
 *
 * Casi gestiti:
 *  - Link valido, prima conferma → 'successo'
 *  - Link valido ma utente già attivato (es. doppio click) → 'gia_attivo'
 *  - Link mancante / type errato / scaduto → 'token_invalido'
 */
export function ConfermaEmail() {
  const navigate = useNavigate()
  const [fase, setFase] = useState<Fase>('caricamento')
  const [erroreMsg, setErroreMsg] = useState<string | null>(null)

  useEffect(() => {
    const hash = window.location.hash
    if (!hash || hash.length < 2) {
      setFase('token_invalido')
      return
    }
    const params = new URLSearchParams(hash.slice(1))
    const token = params.get('access_token')
    const type = params.get('type')
    const errSupabase = params.get('error') || params.get('error_description')

    if (errSupabase) {
      setErroreMsg(errSupabase)
      setFase('token_invalido')
      return
    }
    // Per la conferma email Supabase usa type=signup; accettiamo anche
    // type=email_change per copertura di flussi futuri di cambio email.
    if (!token || (type !== 'signup' && type !== 'email_change')) {
      setFase('token_invalido')
      return
    }

    setFase('attivazione')

    api.post<{ dati: { messaggio: string; era_gia_attivo: boolean } }>(
      '/auth/conferma-email',
      { access_token: token },
    )
      .then((res) => {
        const eraGiaAttivo = res.data?.dati?.era_gia_attivo === true
        setFase(eraGiaAttivo ? 'gia_attivo' : 'successo')
        // Pulisci l'hash dall'URL per evitare che ricaricamenti rifacciano
        // la stessa POST (rate-limit / log rumore)
        window.history.replaceState({}, '', '/auth/callback')
        setTimeout(() => navigate('/accedi?messaggio=email_confermata'), 3000)
      })
      .catch((err) => {
        setErroreMsg(messaggioErrore(err))
        setFase('token_invalido')
      })
  // mount-only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Render: stato caricamento ─────────────────────────────────
  if (fase === 'caricamento' || fase === 'attivazione') {
    return (
      <div className={styles.wrap}>
        <div className={styles.card} style={{ textAlign: 'center' }}>
          <div className={styles.brand} style={{ justifyContent: 'center' }}>
            <div className={styles.logoBox}>RC</div>
            <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
          </div>
          <div
            style={{
              width: 48, height: 48,
              border: '3px solid var(--border)',
              borderTopColor: 'var(--accent)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '8px auto 16px',
            }}
          />
          <h2 className={styles.titolo} style={{ textAlign: 'center', fontSize: 18 }}>
            {fase === 'caricamento' ? 'Verifica in corso…' : 'Attivazione account…'}
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 8 }}>
            {fase === 'caricamento'
              ? 'Stiamo verificando il tuo indirizzo email.'
              : 'Quasi fatto! Stiamo configurando il tuo profilo.'}
          </p>
        </div>
      </div>
    )
  }

  // ─── Render: successo (prima attivazione) ──────────────────────
  if (fase === 'successo') {
    return (
      <div className={styles.wrap}>
        <div className={styles.card}>
          <div className={styles.brand}>
            <div className={styles.logoBox}>RC</div>
            <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
          </div>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--success-soft)', color: 'var(--success)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <CheckCircle2 size={28} />
          </div>
          <h1 className={styles.titolo} style={{ textAlign: 'center' }}>
            Email confermata!
          </h1>
          <p className={styles.descrizione} style={{ textAlign: 'center' }}>
            Il tuo account è ora attivo. Verrai reindirizzato al login tra qualche secondo.
          </p>
          <div style={{ marginTop: 16 }}>
            <Link to="/accedi?messaggio=email_confermata">
              <Button taglia="lg" style={{ width: '100%' }}>
                Vai al login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // ─── Render: già attivo (doppio click sul link) ────────────────
  if (fase === 'gia_attivo') {
    return (
      <div className={styles.wrap}>
        <div className={styles.card}>
          <div className={styles.brand}>
            <div className={styles.logoBox}>RC</div>
            <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
          </div>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--info-soft)', color: 'var(--info)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <CheckCircle2 size={28} />
          </div>
          <h1 className={styles.titolo} style={{ textAlign: 'center' }}>
            Account già attivo
          </h1>
          <p className={styles.descrizione} style={{ textAlign: 'center' }}>
            Il tuo account è già stato confermato in precedenza. Verrai
            reindirizzato al login tra qualche secondo.
          </p>
          <div style={{ marginTop: 16 }}>
            <Link to="/accedi">
              <Button taglia="lg" style={{ width: '100%' }}>
                Vai al login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // ─── Render: link invalido / scaduto / errore ──────────────────
  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.logoBox}>RC</div>
          <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
        </div>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'var(--warning-soft)', color: 'var(--warning)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <AlertTriangle size={28} />
        </div>
        <h1 className={styles.titolo} style={{ textAlign: 'center' }}>
          Link non valido o scaduto
        </h1>
        <p className={styles.descrizione} style={{ textAlign: 'center' }}>
          {erroreMsg ?? 'Il link di conferma non è più valido. I link scadono dopo 24 ore.'}
        </p>
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Link to="/registrati">
            <Button taglia="lg" style={{ width: '100%' }}>
              Registrati di nuovo
            </Button>
          </Link>
          <Link to="/accedi" className={styles.link} style={{ textAlign: 'center' }}>
            <ArrowLeft size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
            Torna al login
          </Link>
        </div>
      </div>
    </div>
  )
}
